import { InlineIcon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Fuse from "fuse.js";
import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getLeanSubagents } from "../../../../api/entity/subagents";
import { brand } from "../../../../context";
import { useApoAuth } from "../../../../hooks";
import { getChatSession } from "../../api/getChatSession";
import { createQuotationRequest } from "./api";
import { LoadingOverlay } from "./LoadingOverlay";
import { QuotationRequestForm } from "./QuotationRequestForm";
import { SubagentSelector } from "./SubagentSelector";
import { TripSummaryCard } from "./TripSummaryCard";

interface FinalizeQuotationRequestModalProps {
  isDarkMode: boolean;
  sessionId: string | null;
  onClose: () => void;
}

export function FinalizeQuotationRequestModal({
  isDarkMode,
  sessionId,
  onClose,
}: FinalizeQuotationRequestModalProps) {
  const {
    data: sessionData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["chatSession", sessionId],
    queryFn: () => {
      if (!sessionId) {
        throw new Error("Session ID is required");
      }
      return getChatSession(sessionId);
    },
    enabled: !!sessionId,
    refetchOnWindowFocus: false,
  });

  const sourceEntity = sessionData?.session?.source_entity || "";
  const userIsSubagent = sourceEntity.includes("___subagent");

  const { data: subagentsData, isFetching: isSubagentsFetching } = useQuery({
    queryKey: ["subagents", sourceEntity],
    queryFn: () => getLeanSubagents(sourceEntity),
    enabled: !!sourceEntity && sourceEntity.includes("___member"),
    refetchOnWindowFocus: false,
  });

  const [subagentSearchQuery, setSubagentSearchQuery] = useState("");
  const [selectedSubagent, setSelectedSubagent] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const queryClient = useQueryClient();
  const { getIdTokenClaims } = useApoAuth();

  // Mutation for submitting quotation request
  const submitQuotationRequestMutation = useMutation({
    mutationFn: async ({ payload }: { payload: any }) => {
      const tokenClaims = await getIdTokenClaims();
      if (!tokenClaims || !("__raw" in tokenClaims)) {
        throw new Error("Authentication token not available");
      }
      const token = (tokenClaims as { __raw: string }).__raw;
      return createQuotationRequest({ payload, token });
    },
    onSuccess: (data) => {
      toast.success(
        `Quotation request submitted successfully! Reference: ${data.data.quotation_request_reference}`,
      );
      // Invalidate chat sessions to refresh the list
      queryClient.invalidateQueries({ queryKey: ["chatSessions"] });
      // Close the modal after successful submission
      onClose();
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.detail || error?.message || "Failed to submit quotation request";
      toast.error(errorMessage);
    },
  });

  // Generate default quotation title from session data
  const defaultQuotationTitle = useMemo(() => {
    if (!sessionData?.session) {
      return "";
    }
    // Use session name as default, or create a fallback title
    return (
      sessionData.session.name ||
      `Quotation Request - ${new Date(sessionData.session.created).toLocaleDateString()}`
    );
  }, [sessionData]);

  // Transform subagents data into options
  const subagentOptions = useMemo(() => {
    const options: Array<{
      id: number;
      label: string;
      value: string;
      name: string;
      email: string;
      legal_title: string;
    }> = [];

    if (subagentsData?.data?.results) {
      for (const sub of subagentsData.data.results) {
        if (!sub) continue;

        let label = "";
        if (sub.name) {
          label += `${sub.name} - `;
        }
        if (sub.legal_title) {
          label += `${sub.legal_title} - `;
        }
        label += sub.email;

        options.push({
          id: sub.id,
          label,
          value: `${sub.id}___subagent`,
          name: sub.name,
          email: sub.email,
          legal_title: sub.legal_title,
        });
      }
      // Sort by label
      options.sort((a, b) => a.label.localeCompare(b.label));
    }

    return options;
  }, [subagentsData]);

  // Filter subagents using FuseJS fuzzy search
  const filteredSubagents = useMemo(() => {
    if (!subagentSearchQuery.trim()) {
      return subagentOptions;
    }

    const fuseOptions = {
      shouldSort: true,
      keys: ["name", "email", "legal_title", "label"],
      threshold: 0.4, // 0.0 = perfect match, 1.0 = match anything
      minMatchCharLength: 1,
    };

    const fuse = new Fuse(subagentOptions, fuseOptions);
    const results = fuse.search(subagentSearchQuery);
    return results.map((result) => result.item);
  }, [subagentOptions, subagentSearchQuery]);

  return (
    <div className="Modal FinalizeQuotationRequestModal" data-dark-mode={isDarkMode}>
      <div
        className={[
          "Modal__card",
          "FinalizeQuotationRequestModal__card",
          "max-h-[80vh]",
          "grid",
          "grid-rows-[auto_1fr_auto]",
          isDarkMode ? "bg-gray-800" : "bg-white",
          "relative",
        ].join(" ")}
      >
        <div
          className={[
            "Modal__card__header",
            "FinalizeQuotationRequestModal__card__header",
            "rounded-t",
            isDarkMode ? "bg-gray-700" : "bg-gray-100",
          ].join(" ")}
        >
          <h6
            className={`text-lg! font-semibold ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}
          >
            Finalize Quotation Request
          </h6>
          <InlineIcon
            icon="ei:close"
            className={[
              "Modal__card__header__icon",
              isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-gray-900",
            ].join(" ")}
            onClick={onClose}
          />
        </div>
        <div
          className={[
            "Modal__card__body",
            "FinalizeQuotationRequestModal__card__body",
            "rounded-b",
            "overflow-y-auto",
            isDarkMode ? "bg-gray-800" : "bg-white",
          ].join(" ")}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isLoading && (
              <p className={isDarkMode ? "text-gray-300" : "text-gray-700"}>
                Loading session data...
              </p>
            )}
            {isError && (
              <p className={isDarkMode ? "text-red-400" : "text-red-600"}>
                Error loading session data. Please try again.
              </p>
            )}
            {sessionData?.session && (
              <>
                <div className="space-y-4">
                  {sessionData.session.trip_summary ? (
                    <TripSummaryCard
                      tripSummary={sessionData.session.trip_summary}
                      isDarkMode={isDarkMode}
                    />
                  ) : (
                    <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                      No trip summary available for this session.
                    </p>
                  )}
                </div>
                <div className="space-y-4">
                  {!userIsSubagent && sourceEntity.includes("___member") && (
                    <SubagentSelector
                      isDarkMode={isDarkMode}
                      subagents={filteredSubagents}
                      isLoading={isSubagentsFetching}
                      selectedSubagent={selectedSubagent}
                      onSubagentChange={setSelectedSubagent}
                      searchQuery={subagentSearchQuery}
                      onSearchChange={setSubagentSearchQuery}
                    />
                  )}
                  <QuotationRequestForm
                    formRef={formRef}
                    isDarkMode={isDarkMode}
                    defaultQuotationTitle={defaultQuotationTitle}
                    selectedSubagent={selectedSubagent}
                    userIsSubagent={userIsSubagent}
                    sourceEntity={sourceEntity}
                    sessionId={sessionId}
                    sessionUsername={sessionData?.session?.username || ""}
                    onSubmit={(payload) => {
                      submitQuotationRequestMutation.mutate({ payload });
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div
          className={`Modal__card__actions grid grid-flow-col auto-cols-max justify-end gap-2 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}
        >
          <button
            type="button"
            className="CancelButton Button"
            data-brand={brand}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="Button"
            data-brand={brand}
            disabled={submitQuotationRequestMutation.isLoading}
            onClick={() => {
              formRef.current?.requestSubmit();
            }}
          >
            {submitQuotationRequestMutation.isLoading ? "Submitting..." : "Submit"}
          </button>
        </div>
        {/* Loading Overlay */}
        {submitQuotationRequestMutation.isLoading && <LoadingOverlay isDarkMode={isDarkMode} />}
      </div>
    </div>
  );
}
